<template>
    <div class="rich-image-message-container">
        <div class="header-message">
            <p>Rich Image ({{ templateOrder + 1 }}/3)</p>
            <div @click="removeRichImageTemplate">
                <i class="bx bx-trash"></i>
            </div>
        </div>
        <div class="message-content">
          <div class="input-group">
            <label class="input-label" for="item-name">
                Item name
                <b-tooltip
                  label="The item name is shown in push notifications and in the user's chat list."
                  position="is-right"
                  class="tooltip-container"
                  type="is-dark"
                  multilined
                >
                  <div label="Right">
                    <i class='bx bx-info-circle'></i>
                  </div>
                </b-tooltip>
            </label>
            <ValidationProvider
              :rules="{
                required: {
                  value: true,
                  message: 'Please enter an item name',
                },
                max: {
                  value: 400
                }
              }"
              v-slot="{ errors }"
            >
              <input
                type="text"
                id="item-name"
                class="item-name-input"
                placeholder="Enter item name"
                @input="updateValueFromTextInput('itemName', $event.target.value)"
                :value="textInputValue.itemName"
              />
              <div class="error-container">
                <p class="error-message">{{ errors[0] }}</p>
              </div>
            </ValidationProvider>
          </div>
          <div class="image-size-dropdown">
            <label class="input-label">
                Image size
            </label>
            <div ref="imageSizeDropdownRef">
              <div class="item-selected" @click="onClickImageSizeDropdownBox">
                <p class="selected-option-text">{{ selectedImageSize.label }}</p>
                <i class="bx bx-caret-up" v-if="isImageSizeDropdownBoxClicked"></i>
                <i class="bx bx-caret-down" v-else></i>
              </div>
              <div class="item-dropdown" v-if="isImageSizeDropdownBoxClicked">
                <div
                v-for="option in imageSizeDropdownOption"
                :key="option.value"
                @click="onClickImageSizeDropdownOption(option.value)"
                class="item-option"
                :class="{'selected-item': option.value === selectedImageSize.value}"
                >
                  <p>{{ option.label }}</p>
                </div>
              </div>
            </div>
            <p class="text-description">The uploaded image must match the selected size.</p>
          </div>
          <div class="image-and-action-container">
            <div>
              <ValidationProvider
                :value="dataURL"
                :rules="isImageFromCopiedMessage ? null : {
                  required: {
                    value: true,
                    message: 'Please upload an image',
                  },
                  multiCustom: {
                    callbacks: [isImageTypeValid, isImageSizeValid, checkUploadingImageSize],
                    messages: [
                      'The file upload must be in JPG, JPEG, or PNG format',
                      `The maximum file upload size is ${maxImageSize} MB`,
                      `Please upload an image with size ${selectedImageSize.label}.`
                    ],
                  }
                }"
                v-slot="{ errors }"
                ref="imageValidationProviderRef"
              >
              <div v-if="isUploadingImage" class="uploading-container">
                <div class="loading-section">
                  <div class="spinner-border" role="status"></div>
                </div>
              </div>
              <div v-else>
                <div v-if="thumbnailImage === null" class="dropzone-container">
                    <vue-dropzone
                      ref="imageDropzoneRef"
                      id="image-dropzone"
                      :options="dropzoneOptions"
                      :useCustomSlot=true
                      @vdropzone-file-added="handleUploadingImage"
                      @vdropzone-thumbnail="handleThumbnail"
                    >
                      <div class="dropzone-custom-input">
                          <div>
                              <i class="bx bx-image"></i>
                          </div>
                          <p class="dropzone-custom-title">Drag an image here
                            <br/>or <span class="text-highlight">Upload a file</span>
                          </p>
                          <p class="text-subtitle">File formats: JPG, JPEG, PNG<br/>File size: Up to 10 MB</p>
                      </div>
                    </vue-dropzone>
                </div>
                <div v-else class="thumbnail-container" style="position: relative;">
                  <img :src="thumbnailImage" alt="thumbnail"/>
                  <div
                    @click="removeThumbnailImage"
                    class="thumbnail-remove"
                  >
                      <i class="bx bx-x" style="color: white; font-size: 16px;"></i>
                  </div>
                </div>
                <div class="error-container">
                  <p class="error-message">{{ errors[0] }}</p>
                </div>
              </div>
              </ValidationProvider>
            </div>
            <div class="action-container">
              <div class="action-dropdown">
                <label class="input-label">
                  Action
                </label>
                <div ref="actionDropdownRef">
                  <div class="item-selected" @click="onClickActionDropdownBox">
                    <p class="selected-option-text">{{ selectedAction.label }}</p>
                    <i class="bx bx-caret-up" v-if="isActionDropdownBoxClicked"></i>
                    <i class="bx bx-caret-down" v-else></i>
                  </div>
                  <div class="item-dropdown" v-if="isActionDropdownBoxClicked">
                    <div
                    v-for="option in actionDropdownOption"
                    :key="option.value"
                    @click="onClickActionDropdownOption(option.value)"
                    class="item-option"
                    :class="{'selected-item': option.value === selectedAction.value}"
                    >
                    <p>{{ option.label }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="selectedAction.value === 'link'" class="input-group">
                <label class="input-label" for="url-input">
                  Url
                  <b-tooltip
                    label="The image will open a link when clicked. The link must start with http:// or https://"
                    position="is-right"
                    class="tooltip-container text-start"
                    type="is-dark"
                    multilined
                  >
                  <div label="Right">
                    <i class='bx bx-info-circle'></i>
                        </div>
                      </b-tooltip>
                </label>
                <ValidationProvider
                  :rules="{
                    required: {
                      value: selectedAction.value === 'link',
                      message: 'Please enter a URL',
                    },
                    custom: {
                      callback: isURLValid,
                      message: 'URL must start with https://',
                    },
                    max: {
                      value: 1000,
                    }
                  }"
                  v-slot="{ errors }"
                >
                  <input
                    type="text"
                    id="url-input"
                    class="item-name-input"
                    placeholder="Enter Url"
                    @input="updateValueFromTextInput('url', $event.target.value)"
                    :value="textInputValue.url"
                  />
                  <div class="error-container">
                    <p class="error-message">{{ errors[0] }}</p>
                  </div>
                </ValidationProvider>
              </div>
            </div>
          </div>
        </div>
    </div>
</template>

<script>
import VueDropzone from 'vue2-dropzone';
import { ValidationProvider } from 'vee-validate';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import aliceApi from '../../../../alice-api';

export default {
  name: 'RichImageMessage',
  components: {
    VueDropzone,
    ValidationProvider,
  },
  props: {
    templateOrder: {
      type: Number,
      required: true,
    },
    errorMessage: {
      type: Object,
      default: () => ({}),
      required: false,
    },
    copiedRichImageMessage: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      dropzoneOptions: {
        url: '/',
        maxFilesize: 10, // MB
        acceptedFiles: 'image/jpeg,image/png,image/jpg',
        maxFiles: 1,
        thumbnailWidth: 250,
        thumbnailHeight: 250,
        autoProcessQueue: false,
      },
      thumbnailImage: null,
      imageSizeDropdownOption: {
        square: {
          value: 'square', label: 'Square (1040x1040pixels)', width: 1040, height: 1040
        },
        landscape: {
          value: 'landscape', label: 'Landscape (1040x700pixels)', width: 1040, height: 700
        },
        portrait: {
          value: 'portrait', label: 'Portrait (1040x1300pixels)', width: 1040, height: 1300
        },
      },
      selectedImageSize: {
        value: 'square',
        label: 'Square (1040x1040pixels)',
      },
      isImageSizeDropdownBoxClicked: false,
      actionDropdownOption: {
        none: { value: 'none', label: 'None' },
        link: { value: 'link', label: 'Link' },
      },
      selectedAction: {
        value: 'none',
        label: 'None',
      },
      isActionDropdownBoxClicked: false,
      dataURL: null,
      textInputValue: {
        itemName: '',
        url: '',
      },
      maxImageSize: 10, // MB,
      isUploadingImage: false,
      isImageFromCopiedMessage: false,
    };
  },
  mounted() {
    if (this.copiedRichImageMessage && Object.keys(this.copiedRichImageMessage).length > 0) {
      this.loadCopiedRichImage();
    } else {
      this.$refs.imageValidationProviderRef.validate(this.dataURL);
    }
    document.addEventListener('mousedown', this.handleClickOutsideRichImageDropdowns, true);
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.handleClickOutsideRichImageDropdowns, true);
  },
  methods: {
    removeRichImageTemplate() {
      this.thumbnailImage = null;
      this.$emit('remove-message-template');
    },
    // ----------Image Management---------- //
    async getImageDimensions(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new window.Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = reject;
          img.src = e.target.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async handleUploadingImage(file) {
      this.isUploadingImage = true;
      const { valid } = await this.$refs.imageValidationProviderRef.validate(file);
      if (!valid) {
        this.dataURL = null;
        this.thumbnailImage = null;
        this.isUploadingImage = false;
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'IMAGE');
      const response = await this.sendUploadedImage(formData);
      if (response) {
        this.dataURL = `${response.data.data.filePath}?w=auto`;
        this.thumbnailImage = this.dataURL;
        this.formatAndEmitRichImageResponse();
      } else {
        this.dataURL = null;
      }
      this.isUploadingImage = false;
    },
    async sendUploadedImage(formData) {
      try {
        const response = await aliceApi.uploadMediaMessage(formData);
        return response;
      } catch (error) {
        console.error('Error uploading file:', error);
        return null;
      }
    },
    handleThumbnail(file, dataURL) {
      this.thumbnailImage = dataURL;
    },
    async removeThumbnailImage() {
      this.isImageFromCopiedMessage = false;
      await this.$nextTick();
      this.thumbnailImage = null;
      this.dataURL = null;
      this.$refs.imageValidationProviderRef.validate(this.dataURL);
      this.formatAndEmitRichImageResponse();
    },
    async checkUploadingImageSize(file) {
      const dimensions = await this.getImageDimensions(file);
      const { width, height } = dimensions;
      const expectedWidth = this.imageSizeDropdownOption[this.selectedImageSize.value].width;
      const expectedHeight = this.imageSizeDropdownOption[this.selectedImageSize.value].height;
      return width === expectedWidth && height === expectedHeight;
    },
    isImageSizeValid(file) {
      const maxSizeBytes = this.maxImageSize * 1024 * 1024;
      return file.size <= maxSizeBytes;
    },
    isImageTypeValid(file) {
      const acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      return acceptedTypes.includes(file.type);
    },
    // ----------Image Management---------- //
    onClickImageSizeDropdownBox() {
      this.isImageSizeDropdownBoxClicked = !this.isImageSizeDropdownBoxClicked;
    },
    onClickActionDropdownBox() {
      this.isActionDropdownBoxClicked = !this.isActionDropdownBoxClicked;
    },
    onClickImageSizeDropdownOption(value) {
      this.isImageSizeDropdownBoxClicked = false;
      if (this.selectedImageSize.value === value) return;
      this.selectedImageSize = this.imageSizeDropdownOption[value];
      this.removeThumbnailImage();
    },
    onClickActionDropdownOption(value) {
      this.selectedAction = this.actionDropdownOption[value];
      this.isActionDropdownBoxClicked = false;
      this.formatAndEmitRichImageResponse();
    },
    updateValueFromTextInput(field, value) {
      if (field in this.textInputValue) {
        this.textInputValue[field] = value;
      }
      this.formatAndEmitRichImageResponse();
    },
    formatAndEmitRichImageResponse() {
      const selectedImageSizeValue = this.selectedImageSize.value;
      const responseData = {
        type: 'IMAGEMAP',
        baseUrl: this.dataURL,
        altText: this.textInputValue.itemName,
        baseSize: {
          width: this.imageSizeDropdownOption[selectedImageSizeValue].width,
          height: this.imageSizeDropdownOption[selectedImageSizeValue].height,
        },
        actions: this.selectedAction.value === 'link'
          ? [
            {
              type: 'URI',
              linkUri: this.textInputValue.url,
              label: this.textInputValue.itemName || null,
              area: {
                x: 0,
                y: 0,
                width: this.imageSizeDropdownOption[selectedImageSizeValue].width,
                height: this.imageSizeDropdownOption[selectedImageSizeValue].height,
              },
            },
          ]
          : [],
      };
      this.$emit('update-rich-image-message', responseData);
    },
    loadCopiedRichImage() {
      this.isImageFromCopiedMessage = true;
      this.dataURL = this.copiedRichImageMessage.baseUrl;
      this.thumbnailImage = this.copiedRichImageMessage.baseUrl;
      this.selectedImageSize = this.formatCopySelectedImageSize(this.copiedRichImageMessage.baseSize);
      this.selectedAction = this.formatSelectedAction(this.copiedRichImageMessage.actions);
      this.textInputValue.itemName = this.copiedRichImageMessage.altText;
      this.formatAndEmitRichImageResponse();
    },
    formatCopySelectedImageSize(baseSize) {
      switch (baseSize.height) {
        case 1040:
          return { value: 'square', label: 'Square (1040x1040pixels)' };
        case 700:
          return { value: 'landscape', label: 'Landscape (1040x700pixels)' };
        case 1300:
          return { value: 'portrait', label: 'Portrait (1040x1300pixels)' };
        default:
          return { value: 'square', label: 'Square (1040x1040pixels)' };
      }
    },
    formatSelectedAction(actions) {
      if (!Array.isArray(actions) || actions.length <= 0) return { value: 'none', label: 'None' };
      this.textInputValue.url = actions[0].linkUri;
      return { value: 'link', label: 'Link' };
    },
    hasErrorMessage(field) {
      return this.errorMessage
        && this.errorMessage[field]
        && this.errorMessage[field].trim().length > 0;
    },
    handleClickOutsideRichImageDropdowns(event) {
      const { imageSizeDropdownRef, actionDropdownRef } = this.$refs;
      if (imageSizeDropdownRef && !imageSizeDropdownRef.contains(event.target)) {
        this.isImageSizeDropdownBoxClicked = false;
      }
      if (actionDropdownRef && !actionDropdownRef.contains(event.target)) {
        this.isActionDropdownBoxClicked = false;
      }
    },
    isURLValid(url) {
      const regex = /^(https?:\/\/).+/;
      return regex.test(url);
    },
  }
};
</script>

<style lang="scss" scoped>
.rich-image-message-container {
    margin-top: 20px;
    border: 1px solid #DADCE4;
    border-radius: 8px;
    p, label{
        margin-bottom: 0px;
    }
    .header-message{
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #F5F6FA;
        font-weight: 500;
        font-size: 14px;
        line-height: 24px;
        letter-spacing: 0px;
        color: #0D173C;
        padding: 10px 16px;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom: 1px solid #DADCE4;
        i{
            font-size: 16px;
            color: #4B5178;
            cursor: pointer;
        }
    }
    .message-content{
        padding: 20px;
        .input-group{
            display: flex;
            flex-direction: column;
            padding-top: 8px;
        }
        .image-size-dropdown{
          position: relative;
        }
        .item-name-input{
            width: 100%;
            height: 40px;
            padding: 8px 16px;
            border-radius: 8px;
            border: 1px solid #DADCE4;
            font-size: 14px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #0D173C;
        }
        select, input:focus-visible{
            outline: none;
        }
        .text-description{
            font-weight: 400;
            font-size: 12px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #868BA6;
            margin-top: 4px;
        }
        .dropzone-container{
            width: 250px;
            height: 250px;
            #image-dropzone{
                width: 100%;
                height: 100%;
                border-radius: 8px;
                border: 1px dashed #8286FF;
                background-color: #EBEAFF;
                .dropzone-custom-input{
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    i{
                        font-size: 40px;
                        color: #4848FF;
                        margin-bottom: 19px;
                    }
                    .dropzone-custom-title{
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 120%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #4B5178;
                        margin-bottom: 24px;
                        .text-highlight{
                            color: #4848FF;
                        }
                    }
                    .text-subtitle{
                        font-weight: 400;
                        font-size: 12px;
                        line-height: 150%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #868BA6;
                    }
                }
            }
        }
        .thumbnail-container{
            width: 250px;
            height: 250px;
            border-radius: 8px;
            overflow: hidden;
            img{
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .thumbnail-remove{
                position: absolute;
                top: 10px;
                right: 10px;
                width: 30px;
                height: 30px;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: #0D173C7A;
                border-radius: 50%;
                cursor: pointer;
            }
        }
        .error-message{
            font-weight: 400;
            font-size: 12px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #EF6767;
            height: 18px;
            width: max-content;
            padding-top: 10px;
        }
        .error-container{
          height: 28px;
        }
        .image-and-action-container{
            display: flex;
            justify-content: space-between;
            align-items: start;
            margin-top: 24px;
            gap: 24px;
            .action-container{
                flex-grow: 1;
                .action-dropdown{
                  position: relative;
                }
            }
        }
    }
    .item-selected{
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        border: 1px solid #DADCE4;
        border-radius: 8px;
        min-width: 130px;
        height: 40px;
        cursor: pointer;
        p{
            font-weight: 400;
            font-size: 14px;
            line-height: 150%;
            letter-spacing: 0px;
            color: #111B49;
        }
        i{
            color: #868BA6;
            font-size: 20px;
        }
    }
    .item-dropdown{
        background-color: #FFFFFF;
        border-radius: 8px;
        border: 1px solid #DADCE4;
        margin-top: 4px;
        padding: 8px 12px;
        min-width: 130px;
        width: 100%;
        position: absolute;
        z-index: 10;
        .item-option{
            padding: 8px 0px;
            cursor: pointer;
        }
        .item-option.selected-item{
            padding: 8px 0px;
            color: #2962DF;
        }
    }
    .input-label{
        font-weight: 500;
        font-size: 14px;
        line-height: 150%;
        letter-spacing: 0px;
        color: #0D173C;
        margin-bottom: 12px;
        label{
            font-weight: 500;
            font-size: 14px;
            line-height: 24px;
            letter-spacing: 0px;
            color: #4B5178;
        }
        i{
            font-size: 16px;
            color: #868BA6;
            margin-left: 5px;
            position: relative;
            top: 2px;
            cursor: pointer;
        }
        i:hover{
            color: #4848FF;;
        }
    }
    .uploading-container{
        padding: 20px;
        .loading-section{
            display: flex;
            justify-content: center;
            align-items: center;
            width: 250px;
            height: 250px;
        }
    }
}
</style>
